import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { GetUserMetadataResponse, UserPreferenceKeyEnum } from "redmond";
import { isCaponeTenant } from "@capone/common";
import { isTripsReplatformBannerEnabledSelector } from "../../reducer/selectors";
import { userApiPrefix } from "../../../../api/v1/paths";
import { config } from "../../../../api/config";

const USER_METADATA_PATH = `${userApiPrefix}/preference/metadata`;
const BANNER_METADATA_KEY = UserPreferenceKeyEnum.hasSeenTripsReplatformBanner;

export interface UseTripsReplatformNoticeBannerProps {
  isConsumerMultiPortal: boolean;
}

export const useTripsReplatformNoticeBanner = ({
  isConsumerMultiPortal,
}: UseTripsReplatformNoticeBannerProps) => {
  const [hasDismissed, setHasDismissed] = useState<boolean | null>(null);

  // The banner-notice variant is resolved from the corporate (COBT) LD project
  // in App.tsx and stored in redux; this selector reads that resolved value.
  const isBannerEnabled = useSelector(isTripsReplatformBannerEnabledSelector);

  const isEligible =
    isCaponeTenant(config.TENANT) && isBannerEnabled && isConsumerMultiPortal;

  useEffect(() => {
    if (!isEligible) {
      return;
    }

    let isCancelled = false;

    axios
      .post<GetUserMetadataResponse>(USER_METADATA_PATH, {
        keys: [BANNER_METADATA_KEY],
      })
      .then((response) => {
        if (isCancelled) {
          return;
        }
        setHasDismissed(
          response.data.userMetadata?.hasSeenTripsReplatformBanner === true
        );
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }
        setHasDismissed(null);
      });

    return () => {
      isCancelled = true;
    };
  }, [isEligible]);

  const dismiss = useCallback(() => {
    setHasDismissed(true);

    axios
      .put(USER_METADATA_PATH, {
        userMetadata: { hasSeenTripsReplatformBanner: true },
      })
      .catch(() => undefined);
  }, []);

  return {
    isOpen: isEligible && hasDismissed === false,
    onDismiss: dismiss,
  };
};
